/**
 * @file PatientProfileView.jsx
 * @author Fernando Ruano
 * @description Component State View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Input, Row, Col } from 'antd';
import { isEmpty, toLower, startCase } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { UnlockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Components
import FormGenerator from '@nephro/components/FormGenerator';
import ButtonOutlined from '@nephro/components/ButtonOutlined';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import {
  onKeepOnlyNumbers,
  capitalizeFirstLetter
} from '@nephro/commons/helpers/string-helper';

// Styles
import styles from './styles.module.css';

let patientData = {};

function PatientProfileView({ patientActions, actions }) {
  const [patientInfo, setPatientInfo] = React.useState(null);
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { getPatientExtraDataById, updatePatient } = patientActions;
  const { setLoading } = actions;

  const arrayButtons = [
    <Col key="1" xs={24} md={12}>
      <Row gutter={10}>
        <Col xs={24} md={12}>
          <div className="w-full"></div>
        </Col>
        <Col xs={24} md={12}>
          <ButtonOutlined
            type="cyan"
            textButton={translate('registration.changePass')}
            classStyles={[{}, 'iphone5:mt-6 md:mt-0'].join(' ')}
            iconComponent={<UnlockOutlined style={{ marginTop: -2 }} />}
            onActionClick={() =>
              history.push(`${location.pathname}/changePassword`, patientInfo)
            }
          />
        </Col>
      </Row>
    </Col>
  ];

  const onGetAvatarLetters = () => {
    const firstName = capitalizeFirstLetter(patientInfo?.first_name || '');
    const lastName = capitalizeFirstLetter(patientInfo?.last_name || '');
    const firstNameLetter = firstName[0];
    const lastNameLetter = lastName[0];
    return `${firstNameLetter}${lastNameLetter}`;
  };

  // Form item list
  const formItems = [
    {
      item: (
        <div>
          <h3 className="text-xl text-left mb-4 text-baxterGray font-bold">
            {translate('Mis datos')}
          </h3>
          <div className="flex flex-row">
            <div className={styles.avatarStyle}>{onGetAvatarLetters()}</div>
            <div>
              <h3 className="text-lg text-left mb-1 text-baxterGray font-bold">
                {startCase(toLower(patientInfo?.first_name))}
              </h3>
              <h3 className="text-lg text-left mb-1 text-baxterGray font-bold">
                {startCase(toLower(patientInfo?.last_name))}
              </h3>
              <h3 className="text-base text-left mb-0 text-placeholder">
                {`${patientData?.age} años`}
              </h3>
            </div>
          </div>
        </div>
      ),
      name: ['avatar'],
      label: '',
      rules: [{ required: false }],
      breakpoints: { xs: 24 }
    },
    {
      item: <Input disabled />,
      name: ['tipo_documento_id'],
      label: translate('patientMg.docType'),
      breakpoints: { xs: 24, sm: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['id_document'],
      label: translate('patientMg.numberId'),
      breakpoints: { xs: 24, sm: 12, lg: 8 }
    },
    {
      item: <Input disabled placeholder={''} />,
      name: ['email'],
      label: translate('patientMg.email'),
      breakpoints: { xs: 24, sm: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['contact_phone_number'],
      label: translate('patientMg.phoneNumber'),
      normalize: onKeepOnlyNumbers,
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['centro'],
      label: translate('patientMg.insuranceCarrier'),
      normalize: onKeepOnlyNumbers,
      breakpoints: { xs: 24, sm: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['aseguradora'],
      label: translate('patientMg.kidneUnit'),
      normalize: onKeepOnlyNumbers,
      breakpoints: { xs: 24, sm: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['modalidad'],
      label: translate('patientMg.modality'),
      normalize: onKeepOnlyNumbers,
      breakpoints: { xs: 24, sm: 12, lg: 8 }
    }
  ];

  //  Function to get and set patient full info
  const onGetFullPatientInfo = (_, response) => {
    if (response?.data) {
      patientData = {
        ...response?.data
      };
      setLoading(false);
    }
  };

  const onGetProfileInfo = async () => {
    const patientInf = await getStorageItem('patientInfo');
    getPatientExtraDataById(
      { id: patientInf?.id_patient },
      onGetFullPatientInfo
    );
    setPatientInfo(patientInf);
  };

  // Function to get on submit form values
  const onFinish = (values) => {
    const data = {
      id: patientInfo.id_patient,
      ...patientInfo,
      contact_phone_number: values.contact_phone_number
    };
    updatePatient(data, onGetProfileInfo);
  };

  React.useEffect(() => {
    onGetProfileInfo();
  }, []);

  return (
    <div className="h-full flex flex-col px-3 py-4">
      {patientInfo && !isEmpty(patientData) && (
        <FormGenerator
          initialValues={patientData}
          onFinish={onFinish}
          items={formItems}
          bottomOptions={arrayButtons}
          optionsFirst={true}
          requiredMark={false}
        />
      )}
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
PatientProfileView.propTypes = {
  patientActions: PropTypes.shape({
    updatePatient: PropTypes.func.isRequired,
    getPatientExtraDataById: PropTypes.func.isRequired,
    getIdentificationTypesArray: PropTypes.func.isRequired
  }).isRequired,
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired
};

export default PatientProfileView;
